import { Box, Grid } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { END_POINTS } from "constants/APIConstant";
import fetch from "auth/FetchInterceptor";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import CircularProgress from "@mui/material/CircularProgress";
import CountdownTimer from "../count-down-timer";
import SnackbarModal from "component/shared/snackbar";
import ReactGA from "react-ga4";
import { parseApiDateTime, toCountDownIso } from "utils/parseApiDateTime";
import { extractAxiosErrorMessage } from "utils/extractApiErrorMessage";

export default function ResetCode() {
  const intl = useIntl();

  const { id } = useParams();

  const navigate = useNavigate();

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => {
      clearTimeout(timeOut);
    };
  }, [id]);

  ReactGA.initialize(END_POINTS.TRACKING_ID);

  useEffect(() => {
    // Send pageview with a custom path
    ReactGA.send({
      hitType: "pageview",
      page: `/resetCode/${id}`,
      title: `ResetCode page`,
    });
  }, [id]);

  // Countdown Time
  // Ensure timer is at least 10 minutes from current time
  const getValidCountDate = () => {
    const storedTimer = localStorage.getItem("countDownCapmasWebsiteTimer");
    const now = Date.now();
    const tenMinutesFromNow = now + 10 * 60 * 1000; // 10 minutes in milliseconds

    if (!storedTimer) {
      // If not exists, set to 10 minutes from now
      const validDate = new Date(tenMinutesFromNow).toISOString();
      localStorage.setItem("countDownCapmasWebsiteTimer", validDate);
      return validDate;
    }

    try {
      const storedTime = parseApiDateTime(storedTimer);

      // Check if stored date is valid and is at least 10 minutes from now
      if (
        isNaN(storedTime) ||
        storedTime <= now ||
        storedTime < tenMinutesFromNow
      ) {
        // Invalid, less than current time, or less than 10 minutes from now
        const validDate = new Date(tenMinutesFromNow).toISOString();
        localStorage.setItem("countDownCapmasWebsiteTimer", validDate);
        return validDate;
      }

      // Normalize offset-bearing API strings to UTC ISO for consistent countdown
      const normalized = toCountDownIso(storedTime) || storedTimer;
      if (normalized !== storedTimer) {
        localStorage.setItem("countDownCapmasWebsiteTimer", normalized);
      }
      return normalized;
    } catch (error) {
      console.error("Error parsing stored timer:", error);
      // On error, set to 10 minutes from now
      const validDate = new Date(tenMinutesFromNow).toISOString();
      localStorage.setItem("countDownCapmasWebsiteTimer", validDate);
      return validDate;
    }
  };

  const countDate = getValidCountDate();

  const [message, setMessage] = useState(null);

  const [loadingBtn, setLoadingBtn] = useState(null);

  // feedback
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // error
  const [openError, setOpenError] = useState(false);
  const handleOpenError = () => {
    setOpenError(true);
  };
  const handleCloseError = () => {
    setOpenError(false);
  };

  const [sendCode, setSendCode] = useState({
    token: id,
    resetCode: "",
  });

  const handleResetCode = (e) => {
    let myCode = { ...sendCode };
    myCode[e.target.name] = e.target.value;
    setSendCode(myCode);
  };

  async function SendCodeForm(e) {
    e.preventDefault();
    setMessage("");
    setLoadingBtn(true);
    await fetch
      .post(`${END_POINTS.RESET_CODE}`, sendCode)
      .then((res) => {
        if (res.state === true) {
          handleOpen();
          setMessage(intl.formatMessage({ id: "successfullySend" }));
          setLoadingBtn(false);
          handleClose();
          navigate(`${APP_PREFIX_PATH}/forgetPass/${id}`);
        }
      })
      .catch((e) => {
        handleOpenError();
        setMessage(extractAxiosErrorMessage(e));
        setLoadingBtn(false);
        handleCloseError();
      });
  }

  return (
    <div className="d-flex align-items-center flex-column py-5">
      <Grid item xs={12} sx={{ textAlign: "center", mb: 3 }}>
        <h2 style={{ color: "var(--main-color)" }}>
          <FormattedMessage id="resetPass" />
        </h2>
      </Grid>
      <Box
        sx={{
          border: "1px solid #E8E8E8",
          borderRadius: "0.75rem",
          width: "50%",
          minWidth: "300px",
        }}
        className={"shadow"}
      >
        <div className="p-5">
          <form onSubmit={SendCodeForm}>
            <div className="mb-4">
              <label className="mb-2 fw-bold" htmlFor={"resetCode"}>
                {intl.formatMessage({ id: "codeLabel" })}
              </label>
              <input
                type={"number"}
                onChange={handleResetCode}
                className="bg-transparent form-control fw-bold mx-auto py-2"
                style={{ fontSize: "14px" }}
                required
                name={"resetCode"}
                id={"resetCode"}
                max={999999}
              />
            </div>
            <div className="text-center">
              <button type="submit" className="main-btn mx-auto">
                <span>
                  {loadingBtn ? (
                    <CircularProgress sx={{ color: "#fff" }} size={26} />
                  ) : (
                    intl.formatMessage({ id: "send" })
                  )}
                </span>
              </button>
            </div>
          </form>

          <div>
            <CountdownTimer countDate={countDate} />
          </div>
        </div>
      </Box>

      {/* feedback */}
      <SnackbarModal
        open={open}
        onClose={handleClose}
        severity="success"
        message={message}
      />

      {/* error */}
      <SnackbarModal
        open={openError}
        onClose={handleCloseError}
        severity="error"
        message={message}
      />
    </div>
  );
}
